// 7章 鉄骨工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_BOLT_HOLE = `
<svg viewBox="0 0 640 220" role="img" aria-label="ボルトの孔径。高力ボルトは呼び径プラス2ミリ、普通ボルトはプラス0.5ミリ、アンカーボルトはプラス5ミリ">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="130" cy="100" r="42"/>
    <circle cx="320" cy="100" r="34"/>
    <circle cx="510" cy="100" r="52"/>
  </g>
  <g fill="currentColor" opacity="0.55">
    <circle cx="130" cy="100" r="30"/>
    <circle cx="320" cy="100" r="30"/>
    <circle cx="510" cy="100" r="30"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="130" y="30">高力ボルト</text>
    <text x="320" y="30">普通ボルト</text>
    <text x="510" y="30">アンカーボルト</text>
  </g>
  <g fill="currentColor" font-size="13" text-anchor="middle">
    <text x="130" y="180">孔径 = d＋2.0mm</text>
    <text x="320" y="180">孔径 = d＋0.5mm</text>
    <text x="510" y="180">孔径 = d＋5.0mm</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.75">
    <text x="320" y="205">●＝ボルト軸（呼び径d）。孔あけは工場でドリルが原則（7.3.8）</text>
  </g>
</svg>`;

const SVG_FRICTION = `
<svg viewBox="0 0 640 250" role="img" aria-label="高力ボルト摩擦接合の原理。ボルトの強い張力で板どうしを締め付け、板の間の摩擦力で力を伝える。すべり係数は0.45以上">
  <g fill="currentColor" opacity="0.85">
    <rect x="60" y="100" width="330" height="18" rx="2"/>
    <rect x="250" y="126" width="330" height="18" rx="2"/>
  </g>
  <g fill="currentColor" opacity="0.5">
    <rect x="250" y="74" width="140" height="18" rx="2"/>
    <rect x="250" y="152" width="140" height="18" rx="2"/>
  </g>
  <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
    <line x1="320" y1="60" x2="320" y2="186"/>
  </g>
  <g fill="currentColor">
    <rect x="300" y="48" width="40" height="14" rx="3"/>
    <rect x="300" y="184" width="40" height="14" rx="3"/>
  </g>
  <g stroke="currentColor" stroke-width="2">
    <line x1="90" y1="109" x2="40" y2="109"/>
    <line x1="550" y1="135" x2="600" y2="135"/>
  </g>
  <g fill="currentColor">
    <polygon points="40,109 54,103 54,115"/>
    <polygon points="600,135 586,129 586,141"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="360" y="42">ボルト張力で板を強く挟む</text>
    <text x="40" y="230">→ 板と板の「摩擦力」で力を伝える（すべり係数 0.45 以上）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.8">
    <text x="40" y="90">引張力</text>
    <text x="560" y="120">引張力</text>
  </g>
</svg>`;

const SVG_HTB_FLOW = `
<svg viewBox="0 0 660 240" role="img" aria-label="高力ボルト締付けの手順。仮ボルトで板を密着させ、ボルト取付け後に一次締め、マーキング、本締めの順で行う。1群のボルトは中央から周辺に向かって締める">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="40" width="130" height="56" rx="8"/>
    <rect x="180" y="40" width="130" height="56" rx="8"/>
    <rect x="340" y="40" width="130" height="56" rx="8"/>
    <rect x="500" y="40" width="140" height="56" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="150" y1="68" x2="172" y2="68"/>
    <line x1="310" y1="68" x2="332" y2="68"/>
    <line x1="470" y1="68" x2="492" y2="68"/>
  </g>
  <g fill="currentColor">
    <polygon points="180,68 168,62 168,74"/>
    <polygon points="340,68 328,62 328,74"/>
    <polygon points="500,68 488,62 488,74"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="85"  y="64">仮ボルトで</text>
    <text x="85"  y="82">板を密着</text>
    <text x="245" y="72">① 一次締め</text>
    <text x="405" y="72">② マーキング</text>
    <text x="570" y="72">③ 本締め</text>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="120" y="130" width="180" height="90" rx="8"/>
  </g>
  <g fill="currentColor">
    <circle cx="210" cy="175" r="8"/>
    <circle cx="170" cy="155" r="6" opacity="0.6"/><circle cx="250" cy="155" r="6" opacity="0.6"/>
    <circle cx="170" cy="195" r="6" opacity="0.6"/><circle cx="250" cy="195" r="6" opacity="0.6"/>
  </g>
  <g stroke="currentColor" stroke-width="1.2" opacity="0.7">
    <line x1="210" y1="175" x2="176" y2="158"/>
    <line x1="210" y1="175" x2="244" y2="158"/>
    <line x1="210" y1="175" x2="176" y2="192"/>
    <line x1="210" y1="175" x2="244" y2="192"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="330" y="165" font-weight="bold">1群の締付け順序：</text>
    <text x="330" y="188">中央 → 周辺へ</text>
    <text x="330" y="210" opacity="0.75">（外から締めると中央に隙間が残る）</text>
  </g>
</svg>`;

const SVG_TORSHIA = `
<svg viewBox="0 0 660 300" role="img" aria-label="トルシア形高力ボルト締付け後の合格確認4点。ピンテールが破断していること。マークのずれで共回りがないこと。ナット回転量が平均プラスマイナス30度の範囲。余長がねじ1山から6山">
  <g fill="currentColor" opacity="0.85">
    <rect x="60" y="60" width="120" height="16" rx="2"/>
    <rect x="60" y="80" width="120" height="16" rx="2"/>
  </g>
  <g stroke="currentColor" stroke-width="8" stroke-linecap="round">
    <line x1="120" y1="46" x2="120" y2="112"/>
  </g>
  <g fill="currentColor">
    <rect x="98" y="104" width="44" height="14" rx="3"/>
  </g>
  <g stroke="currentColor" stroke-width="3" stroke-dasharray="3 3" opacity="0.6">
    <line x1="120" y1="118" x2="120" y2="140"/>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="150" y="135">ピンテール（破断して脱落＝所定張力の証拠）</text>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="420" cy="90" r="40"/>
    <circle cx="420" cy="90" r="22"/>
  </g>
  <g stroke="currentColor" stroke-width="3">
    <line x1="420" y1="50" x2="420" y2="28"/>
    <line x1="436" y1="74" x2="452" y2="60"/>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="480" y="40">マーク：ナットだけ回転＝正常</text>
    <text x="480" y="62">（全部一緒に回る＝共回り→NG）</text>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="40" y="190">締付け後の合格確認（トルシア形・7.4.8）</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="40" y="216">① ピンテールが破断している</text>
    <text x="40" y="240">② マークのずれで共回り・軸回りがない</text>
    <text x="360" y="216">③ ナット回転量：群の平均±30°の範囲</text>
    <text x="360" y="240">④ 余長：ねじ1山〜6山</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="278">不合格のセットは新品に交換（再使用禁止）。確認結果は記録して監督職員の検査を受ける</text>
  </g>
</svg>`;

const SVG_WELD_TYPES = `
<svg viewBox="0 0 660 260" role="img" aria-label="溶接の3種類。完全溶込み溶接は開先を設けて板厚全部を溶接し母材と同等の強度。部分溶込み溶接は板厚の一部だけ溶接。隅肉溶接は直交する板の入隅を三角形に溶接する">
  <g fill="currentColor" opacity="0.8">
    <rect x="30" y="100" width="70" height="30"/>
    <rect x="130" y="100" width="70" height="30"/>
    <rect x="260" y="100" width="70" height="30"/>
    <rect x="360" y="100" width="70" height="30"/>
    <rect x="490" y="100" width="90" height="26"/>
    <rect x="520" y="126" width="26" height="70"/>
  </g>
  <g fill="currentColor" opacity="0.45">
    <path d="M100 100 L115 92 L130 100 L130 130 L100 130 Z"/>
    <path d="M330 100 L345 95 L360 100 L360 114 L330 114 Z"/>
    <path d="M546 126 L572 126 L546 152 Z"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="115" y="50">完全溶込み溶接</text>
    <text x="345" y="50">部分溶込み溶接</text>
    <text x="535" y="50">隅肉溶接</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="115" y="70">板厚の全部を溶接</text>
    <text x="345" y="70">板厚の一部を溶接</text>
    <text x="535" y="70">入隅を三角形に溶接</text>
    <text x="115" y="160">開先をとり裏はつり又は</text>
    <text x="115" y="176">裏当て金で完全に溶かす</text>
    <text x="345" y="160">のど厚は溶けた深さ分</text>
    <text x="535" y="216">サイズの2倍を有効長さに加える</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="245">柱梁の重要な継手は完全溶込み溶接。エンドタブで始端・終端の欠陥を母材の外に逃がす（7.6.7）</text>
  </g>
</svg>`;

const SVG_WELD_DEFECTS = `
<svg viewBox="0 0 660 260" role="img" aria-label="溶接欠陥の名前。アンダーカットはビード脇の母材が掘れた欠陥。ピットは表面の小穴。ブローホールは内部の気泡。オーバーラップは溶着金属が母材に重なっただけの部分">
  <g fill="currentColor" opacity="0.8">
    <rect x="60" y="140" width="540" height="26"/>
  </g>
  <g fill="currentColor" opacity="0.5">
    <path d="M240 140 C260 108 400 108 420 140 Z"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.8">
    <path d="M236 140 q-8 -8 -16 -2"/>
    <path d="M424 140 q8 -8 16 -2"/>
  </g>
  <g fill="currentColor">
    <circle cx="300" cy="122" r="4" opacity="0.9"/>
    <circle cx="350" cy="150" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/>
  </g>
  <g stroke="currentColor" stroke-width="1.2" opacity="0.7">
    <line x1="228" y1="132" x2="180" y2="80"/>
    <line x1="300" y1="118" x2="300" y2="66"/>
    <line x1="350" y1="144" x2="430" y2="70"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="90" y="70">アンダーカット</text>
    <text x="255" y="56">ピット（表面の小穴）</text>
    <text x="435" y="60">ブローホール（内部の気泡）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.85">
    <text x="90" y="90">ビード脇の掘れ込み</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="60" y="205">・表面の欠陥 → 外観試験（全数）で発見。割れの疑いは浸透探傷・磁粉探傷試験</text>
    <text x="60" y="228">・内部の欠陥（ブローホール・溶込み不良等） → 超音波探傷試験（UT）で発見</text>
  </g>
</svg>`;

const SVG_WELD_TEMP = `
<svg viewBox="0 0 640 200" role="img" aria-label="気温による溶接の措置。氷点下5度未満は溶接禁止。氷点下5度から5度は溶接線から100ミリ程度の範囲を加熱すれば溶接できる。5度を超えれば通常どおり">
  <g fill="currentColor" opacity="0.85">
    <rect x="60" y="80" width="160" height="34" rx="4"/>
  </g>
  <g fill="currentColor" opacity="0.5">
    <rect x="220" y="80" width="180" height="34" rx="4"/>
  </g>
  <g fill="currentColor" opacity="0.25">
    <rect x="400" y="80" width="180" height="34" rx="4"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="140" y="66">−5℃未満</text>
    <text x="310" y="66">−5℃ 〜 5℃</text>
    <text x="490" y="66">5℃超</text>
  </g>
  <g fill="currentColor" font-size="12.5" text-anchor="middle">
    <text x="140" y="140">溶接禁止</text>
    <text x="310" y="140">溶接線から100mm程度を</text>
    <text x="310" y="158">加熱すれば可</text>
    <text x="490" y="140">通常どおり施工可</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="188">雨・雪で母材が濡れている場合、溶接に影響する風がある場合も原則禁止（養生すれば可）（7.6.8）</text>
  </g>
</svg>`;

const SVG_STUD = `
<svg viewBox="0 0 660 260" role="img" aria-label="頭付きスタッドの仕上り基準。仕上り高さは所定高さプラスマイナス2ミリ、傾きは5度以内、カラーが全周に形成されていること。抜取りで15度打撃曲げ試験を行う">
  <g fill="currentColor" opacity="0.8">
    <rect x="60" y="180" width="250" height="20"/>
  </g>
  <g stroke="currentColor" stroke-width="10" stroke-linecap="round">
    <line x1="150" y1="180" x2="150" y2="80"/>
  </g>
  <g fill="currentColor">
    <rect x="126" y="64" width="48" height="16" rx="4"/>
    <path d="M136 180 q14 -14 28 0 Z" opacity="0.6"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="200" y1="80" x2="200" y2="180"/>
    <line x1="192" y1="80" x2="208" y2="80"/>
    <line x1="192" y1="180" x2="208" y2="180"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="216" y="130" font-weight="bold">高さ：±2mm</text>
    <text x="216" y="152">傾き：5°以内</text>
    <text x="60" y="228" font-weight="bold">カラー（根元の丸い盛上り）が全周にあること</text>
  </g>
  <g stroke="currentColor" stroke-width="8" stroke-linecap="round" opacity="0.6">
    <line x1="480" y1="180" x2="510" y2="84"/>
  </g>
  <g fill="currentColor" opacity="0.8">
    <rect x="400" y="180" width="200" height="20"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M480 120 A62 62 0 0 1 505 96"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="520" y="120" font-weight="bold">打撃曲げ試験</text>
    <text x="520" y="142">15°曲げて割れ等が</text>
    <text x="520" y="162">なければ合格</text>
    <text x="400" y="228" opacity="0.85">抜取り：100本＋端数を1ロットに1本以上</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="60" y="250">合格したスタッドは曲がったまま使用してよい（欠陥がなければ）（7.7.6）</text>
  </g>
</svg>`;

const SVG_BASE = `
<svg viewBox="0 0 660 260" role="img" aria-label="柱底均しモルタルA種工法。柱建込み前にベースプレート中央下部に硬練りモルタルを所定高さに塗り、建込み後にベースプレート周囲に型枠を設け無収縮モルタルをあふれ出るまで圧入する">
  <g fill="currentColor" opacity="0.25">
    <rect x="80" y="180" width="480" height="50"/>
  </g>
  <g fill="currentColor" opacity="0.9">
    <rect x="250" y="60" width="60" height="100"/>
    <rect x="200" y="160" width="160" height="12"/>
  </g>
  <g fill="currentColor" opacity="0.55">
    <path d="M255 172 h50 v8 h-50 Z"/>
    <rect x="200" y="172" width="160" height="8" opacity="0.35"/>
  </g>
  <g stroke="currentColor" stroke-width="4" stroke-linecap="round">
    <line x1="225" y1="120" x2="225" y2="185"/>
    <line x1="335" y1="120" x2="335" y2="185"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="380" y="80" font-weight="bold">A種工法（標準）</text>
    <text x="380" y="106">① 中央下部に硬練りモルタルを塗る</text>
    <text x="380" y="130">② 柱を建て込む</text>
    <text x="380" y="154">③ 周囲に型枠を設け、無収縮モルタルを</text>
    <text x="392" y="176">あふれ出るまで圧入する</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.85">
    <text x="80" y="120">柱</text>
    <text x="150" y="158">ベースプレート</text>
    <text x="90" y="205">基礎コンクリート（レイタンス除去＋目荒し）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="80" y="250">アンカーボルト：二重ナット＋座金、ねじ山3山以上出す（コンクリート埋込み部は二重ナット不要）</text>
  </g>
</svg>`;

const SVG_KARI_BOLT = `
<svg viewBox="0 0 660 240" role="img" aria-label="仮ボルトの本数。通常の接合部は1群のボルト数の3分の1以上かつ2本以上。混用接合や併用継手は2分の1以上かつ2本以上。現場溶接のエレクションピースは高力ボルトを全数締め付ける">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="24"  y="40" width="195" height="140" rx="10"/>
    <rect x="233" y="40" width="195" height="140" rx="10"/>
    <rect x="442" y="40" width="195" height="140" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="121" y="68">通常の接合部</text>
    <text x="330" y="68">混用接合・併用継手</text>
    <text x="539" y="68">現場溶接の</text>
    <text x="539" y="86">エレクションピース</text>
  </g>
  <g fill="currentColor" font-size="12.5" text-anchor="middle">
    <text x="121" y="104">1群の</text>
    <text x="121" y="128" font-weight="bold">1/3以上</text>
    <text x="121" y="152">かつ2本以上</text>
    <text x="330" y="104">1群の</text>
    <text x="330" y="128" font-weight="bold">1/2以上</text>
    <text x="330" y="152">かつ2本以上</text>
    <text x="539" y="122" font-weight="bold">高力ボルトで</text>
    <text x="539" y="146" font-weight="bold">全数締付け</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="24" y="212">仮ボルトは本接合と同軸径の普通ボルト等（損傷のないもの）。本接合ボルトの流用はしない（7.10.5）</text>
  </g>
</svg>`;

const ch07: Record<string, Commentary> = {
  "7.1.1": {
    plainSummary:
      "この章は構造上主要な部材に鋼材を使う工事（鉄骨工事）のルール。1章とセットで適用する。",
  },

  "7.1.2": {
    plainSummary:
      "鉄骨工事のゴールは3つ。①所定の材料 ②所定の形状・寸法で所定の位置に架構 ③有害な欠陥がなく、接合部が力を伝えられること。",
    why: "鉄骨は「工場で作って現場で組む」工事です。だから品質の大半は工場（3節 工作、4〜7節 接合）で決まり、現場（10節 建方）で仕上がります。この構成を頭に入れると7章が読みやすくなります。",
  },

  "7.1.3": {
    plainSummary:
      "鉄骨製作工場の加工能力（グレード）は特記による。選定した工場の能力を証明する資料を提出して承諾を受け、品質管理の記録も提出する。",
    why: "鉄骨製作工場には国土交通大臣認定のグレード（J・R・M・H・S）があり、扱える鋼材や溶接条件が違います。特記で指定されたグレード以上の工場を選ぶのが基本です。",
    glossary: [
      {
        term: "鉄骨製作工場のグレード",
        meaning:
          "J（小規模）→R→M→H→S（最上位）の5段階の大臣認定制度。建物規模・鋼材強度に応じて要求される。",
      },
    ],
  },

  "7.1.4": {
    plainSummary:
      "鉄骨製作工場に配置する施工管理技術者（配置は特記による）は、鉄骨造の設計・施工の指導と品質管理ができる者とする。",
  },

  "7.2.1": {
    plainSummary:
      "鋼材はSS400、SM、SN400/490などJIS規格品から特記で指定される。",
    why: "記号の意味を覚えましょう。SS=一般構造用、SM=溶接構造用、SN=建築構造用（耐震性を考慮した建築専用規格）。数字は引張強さ（N/mm²）。現在の建築の主要構造部はSN材が標準です。SN材のB・C種は溶接性・塑性変形能力が保証されています。",
    glossary: [
      {
        term: "SN材（建築構造用圧延鋼材）",
        meaning:
          "建築の耐震設計を前提に作られた鋼材規格。A種（溶接しない部材用）、B種（一般）、C種（板厚方向の力を受ける部材用）がある。",
      },
    ],
  },

  "7.2.2": {
    plainSummary:
      "高力ボルトはトルシア形（S10T）・JIS形（F10T）・溶融亜鉛めっき（F8T相当）の3種類で、種類は特記による。ボルト長さは締付け長さに規定値（M20なら30mm等）を加えて選ぶ。",
    glossary: [
      {
        term: "トルシア形高力ボルト（S10T）",
        meaning:
          "先端のピンテールが所定トルクで破断することで張力管理ができるボルト。専用レンチで締める。現在の主流。",
      },
      {
        term: "F10T",
        meaning: "JIS形高力ボルトの強度区分。引張強さ10tf/cm²級の意味。",
      },
    ],
  },

  "7.2.3": {
    plainSummary:
      "普通ボルトは六角ボルト（強度区分4.6又は4.8）。締付け後にナットの外へねじが3山以上出る長さを選ぶ。座金はボルトに相応したもの。",
  },

  "7.2.4": {
    plainSummary:
      "アンカーボルトは構造用（JIS B 1220）と建方用（SS材）に分かれ、種類は特記による。",
    glossary: [
      {
        term: "アンカーボルト",
        meaning:
          "基礎コンクリートに埋め込み、鉄骨柱脚を固定するボルト。構造耐力を負担する「構造用」と、建方時の固定用の「建方用」がある。",
      },
    ],
  },

  "7.2.5": {
    plainSummary:
      "溶接棒・ワイヤは母材の種類・寸法・溶接条件に相応したものをJIS規格から選ぶ。シールドガスはワイヤに相応したもの。",
    why: "溶接材料は母材とのマッチングが命です。強い母材に弱い溶接材料を使えば継手が弱点になります。また吸湿した溶接棒は欠陥（ブローホール）の原因になるため、7.6.4で乾燥管理が求められます。",
  },

  "7.2.6": {
    plainSummary:
      "ターンバックル（ブレースの張力調整金物）はJIS品。特記がなければ羽子板ボルト＋割枠式胴とする。",
  },

  "7.2.7": {
    plainSummary:
      "床構造用のデッキプレートはJIS G 3352により、材質・形状・寸法は特記による。",
    glossary: [
      {
        term: "デッキプレート",
        meaning:
          "波形に成形した鋼板。床の型枠兼用や、コンクリートと一体化する合成スラブに使う。",
      },
    ],
  },

  "7.2.8": {
    plainSummary: "スタッドはJIS B 1198の頭付きスタッドで、種類等は特記による。",
    glossary: [
      {
        term: "頭付きスタッド",
        meaning:
          "梁の上に溶接する頭付きの鋼棒。鉄骨梁とコンクリートスラブを一体化（合成梁）させるためのシアコネクタ。",
      },
    ],
  },

  "7.2.9": {
    plainSummary:
      "柱底均しモルタルはセメント1：砂2。無収縮モルタルとする場合は圧縮強度が材齢3日で25N/mm²以上・28日で45N/mm²以上など品質規定がある。",
    glossary: [
      {
        term: "無収縮モルタル",
        meaning:
          "膨張材の効果で硬化時に収縮しないモルタル。ベースプレート下に隙間なく充填するために使う。",
      },
    ],
  },

  "7.2.10": {
    plainSummary:
      "鋼材の品質証明は規格品証明書（ミルシート）による。板厚方向に引張力を受ける鋼板は超音波探傷試験の適用が特記される。",
    why: "板厚方向の引張は、鋼板内部の非金属介在物による「ラメラテア（層状剥離）」のリスクがあるため、特記によりUT検査で健全性を確認します。",
  },

  "7.3.1": {
    plainSummary: "この節は鉄骨の製作（工場での工作）に適用する。",
  },

  "7.3.2": {
    plainSummary:
      "ボルトの縁端距離・ボルト間隔・ゲージは特記による。現寸図は必要に応じて作成する。",
    glossary: [
      {
        term: "ゲージ",
        meaning: "部材軸方向に直角なボルト列の基準線間隔。",
      },
      {
        term: "縁端距離",
        meaning: "ボルト孔中心から部材縁までの距離。小さすぎると縁が破断する。",
      },
    ],
  },

  "7.3.3": {
    plainSummary:
      "製作精度はJASS 6の鉄骨精度検査基準による。アンダーカットと突合せ継手の食違いは告示第1464号による。",
    glossary: [
      {
        term: "JASS 6",
        meaning:
          "日本建築学会の建築工事標準仕様書・鉄骨工事編。鉄骨の製作・施工の実務標準。",
      },
    ],
  },

  "7.3.4": {
    plainSummary:
      "けがき（加工の墨付け）は工作図・現寸図により正確に。490N/mm²級以上の高張力鋼や曲げ加工の外側にはポンチ・たがねの打こんを残さない。",
    why: "打こんの小さな傷が応力集中点となり、疲労破壊や脆性破壊の起点になるからです。溶接で溶ける箇所や切断で除去される箇所は例外です。",
  },

  "7.3.5": {
    plainSummary:
      "切断面は材軸に垂直。ガス切断は自動ガス切断とする。厚さ13mm以下ならせん断切断可（ただし主要部材の自由端・溶接部には使わない）。",
    why: "せん断切断（シャーリング）は断面がむしれやすく、微細な亀裂が残ることがあります。力の流れる溶接部や自由端に使わないのはそのためです。",
  },

  "7.3.6": {
    plainSummary:
      "素材や部材のひずみは、各工程で材質を損なわないように矯正する。",
  },

  "7.3.7": {
    plainSummary:
      "鉄筋の貫通孔の最大径が決まっている（D16→28mm、D22→35mm、D25→38mmなど）。",
    why: "SRC造などで鉄骨ウェブに鉄筋を通す孔です。大きすぎると断面欠損、小さすぎると鉄筋が通らないため、呼び名ごとに最大値が規定されています。",
  },

  "7.3.8": {
    plainSummary:
      "孔あけは工場でドリルが原則（板厚13mm以下の普通ボルト等はせん断孔あけ可）。孔径は高力ボルト＝呼び径＋2.0mm、普通ボルト＝＋0.5mm、アンカーボルト＝＋5.0mm。",
    why: "高力ボルトは摩擦接合なので多少のクリアランス（+2mm）でよく、普通ボルトはせん断接合で軸が直接力を受けるため隙間を小さく（+0.5mm）します。アンカーボルトは建方時の位置調整のため大きめ（+5mm）です。「接合の仕組みの違い＝孔径の違い」として覚えましょう。",
    diagrams: [
      {
        svg: SVG_BOLT_HOLE,
        caption: "ボルトの種類と孔径（表7.3.2）",
      },
    ],
  },

  "7.3.9": {
    plainSummary:
      "仮設のために鉄骨へ補助材の取付けや孔あけをする場合は、監督職員の承諾が必要。溶接する場合は関連工事溶接（7.6.9）に準ずる。",
    why: "本体構造の鉄骨に勝手に孔をあけたり溶接したりすれば断面欠損・材質変化になります。仮設といえども承諾必須です。",
  },

  "7.3.10": {
    plainSummary:
      "仮組（工場で一度組み立てて精度確認すること）の実施は特記による。実施時は施工計画書を作成。",
  },

  "7.3.11": {
    plainSummary:
      "鉄骨製作用の基準巻尺はJIS 1級とし、現場用基準巻尺との誤差が工事に支障ないことを確認する。",
    why: "工場と現場で巻尺が違えば、工場で正確に作っても現場で合いません。着工時に「テープ合わせ」を行うのはこのためです。",
  },

  "7.4.1": {
    plainSummary:
      "この節はトルシア形・JIS形高力ボルトの摩擦接合に適用する。",
  },

  "7.4.2": {
    plainSummary:
      "摩擦面はすべり係数0.45以上を確保する。方法は①ミルスケール除去後に一様に赤錆を発生させる、または②ブラスト処理（表面粗度50μmRz以上）。まくれ・へこみは除去する。",
    why: "高力ボルト摩擦接合は「ボルトで板を強烈に挟み、板と板の摩擦で力を伝える」接合です。摩擦面がツルツルでは滑ってしまうため、赤錆やブラストでざらざらの面を作ります。塗料や油が付けば摩擦力が落ちるので厳禁です。",
    points: [
      "摩擦面には塗装しない（7.8.2でも塗装除外部位）",
      "浮き錆・油・じんあいは組立前に除去",
      "1mmを超える肌すきにはフィラープレート",
    ],
    glossary: [
      {
        term: "すべり係数",
        meaning: "摩擦接合面の滑りにくさの指標。0.45以上が必要。",
      },
      {
        term: "ミルスケール（黒皮）",
        meaning: "圧延時にできる鋼材表面の酸化被膜。摩擦面では除去する。",
      },
    ],
    diagrams: [
      {
        svg: SVG_FRICTION,
        caption: "高力ボルト摩擦接合の原理（7.4.2）",
      },
    ],
  },

  "7.4.3": {
    plainSummary:
      "標準ボルト張力はねじの呼びごとに規定（M20で182kN、M22で226kNなど）。",
  },

  "7.4.4": {
    plainSummary:
      "高力ボルトのセットは未開封で搬入し、使用直前に開封。使わなかったものは再包装して保管。試験・調整に使ったボルトは本接合に使わない。",
    why: "高力ボルトはボルト・ナット・座金の「セット」で潤滑状態まで管理された精密部品です。雨ざらしや長期開封で錆びると、同じトルクでも張力が変わってしまいます。",
  },

  "7.4.5": {
    plainSummary:
      "締付け作業に先立ち、工事で採用する締付け施工法の確認（試し締めによる張力確認）を行う。",
  },

  "7.4.6": {
    plainSummary:
      "摩擦面に浮き錆・油・塗料・じんあいがあれば除去してから組み立てる。1mm超の肌すきにはフィラープレート、1/20以上の傾斜には勾配座金。孔が合わずボルトが入らない場合は添え板等を交換する。",
    points: [
      "孔が合わないときにリーマーで無理に広げない（添え板交換が原則）",
    ],
    glossary: [
      {
        term: "肌すき",
        meaning: "接合する板の厚さの差などでできる隙間。1mm超はフィラープレートで埋める。",
      },
    ],
  },

  "7.4.7": {
    plainSummary:
      "締付けは「仮ボルトで密着→一次締め→マーキング→本締め」の順。1群の締付けは中央から周辺へ。一次締めトルクはM20・M22で150N・m程度。本締めはトルシア形＝ピンテール破断まで、JIS形＝ナット回転法（120°）等。",
    why: "外側から締めると中央に板の浮きが閉じ込められ、密着不良になります。だから「中央から外へ」。マーキングは本締め後の検査（共回りチェック）のための基準線です。この手順は高力ボルト施工の一丁目一番地なので体で覚えましょう。",
    points: [
      "一次締め→マーキング→本締めの順序を絶対に守る",
      "ナット回転法の規定回転量は120°（M12のみ60°）",
      "着氷のおそれがあるときは締め付けない",
    ],
    diagrams: [
      {
        svg: SVG_HTB_FLOW,
        caption: "高力ボルト締付けの手順と順序（7.4.7）",
      },
    ],
  },

  "7.4.8": {
    plainSummary:
      "締付け後の合格確認：①ピンテール破断 ②マークずれで共回り・軸回りなし ③ナット回転量が平均±30°以内 ④余長ねじ1〜6山。不合格セットは新品交換、再使用禁止。結果を記録し検査を受ける。",
    why: "高力ボルトの張力は目に見えないため、「ピンテール破断」「マークのずれ方」「回転量」という間接的な証拠で全数確認します。一度締めたボルトは軸力で伸びているため、緩めて締め直すことはできません（＝交換）。",
    diagrams: [
      {
        svg: SVG_TORSHIA,
        caption: "締付け後の確認ポイント（7.4.8）",
      },
    ],
  },

  "7.4.9": {
    plainSummary:
      "締付け機器・確認用機器は点検整備されたものを使う。トルクコントロール法の場合は毎日作業開始前に機器を調整し記録する。",
  },

  "7.5.1": {
    plainSummary: "この節は普通ボルトによるせん断接合に適用する。",
    why: "普通ボルト接合は軸が直接せん断力を受ける接合で、主に母屋・胴縁などの二次部材に使われます。",
  },

  "7.5.2": {
    plainSummary:
      "緩み・ずれのないよう締め付け、有効な戻止め（ダブルナット等）をする。座金をボルト頭とナットの下に使い、締付け後にねじ3山以上出ていることを確認。",
    points: [
      "普通ボルトは振動で緩む→戻止めが必須",
      "「3山以上」は普通ボルト・アンカーボルト共通のキーワード",
    ],
  },

  "7.6.1": {
    plainSummary:
      "この節は手溶接（被覆アーク）・半自動溶接（ガスシールドアーク等）・自動溶接（サブマージアーク等）による溶接接合に適用する。",
    glossary: [
      {
        term: "半自動溶接",
        meaning:
          "ワイヤ送給は自動、トーチ操作は手動の溶接。鉄骨工場の主力工法（CO2溶接など）。",
      },
    ],
  },

  "7.6.2": {
    plainSummary:
      "溶接作業には、JIS Z 3410に基づく溶接管理ができる施工管理技術者を配置する。",
  },

  "7.6.3": {
    plainSummary:
      "溶接作業は技能資格者が行う。手溶接はJIS Z 3801、半自動はJIS Z 3841の検定による。技量に疑いが生じた場合は試験で適否を判定する。",
    points: [
      "溶接工の資格証と従事する溶接方法・姿勢が合っているか確認",
      "技量付加試験の有無は特記を確認",
    ],
  },

  "7.6.4": {
    plainSummary:
      "開先の形状は特記により、加工は自動ガス切断か機械加工。吸湿の疑いがある溶接材料は乾燥してから使う。",
    glossary: [
      {
        term: "開先（かいさき）",
        meaning:
          "完全溶込み溶接のために母材の端部を斜めに加工した溝。グルーブとも言う。",
      },
    ],
  },

  "7.6.5": {
    plainSummary:
      "組立はルート間隔を確保し隙間なく密着させる。高力ボルトと溶接を併用する場合は高力ボルトを先に。組立溶接（仮付け）は隅角部・本溶接の始終点を避け、最小ビード長さ（板厚6mm超で40mm等）を守る。",
    why: "溶接後に高力ボルトを締めても、溶接で板が固定されていて密着させられないため「ボルトが先」です。組立溶接は小さくても本物の溶接。冷却が速く割れやすいので、最小ビード長さが決められています。",
    glossary: [
      {
        term: "ルート間隔",
        meaning: "突合せ溶接で母材どうしの間にとる隙間。溶込みを確保するために必要。",
      },
      {
        term: "組立溶接（仮付け溶接）",
        meaning: "本溶接の前に部材を保持するための短い溶接。",
      },
    ],
  },

  "7.6.6": {
    plainSummary:
      "溶接前に、水分・油・スラグ・塗料・錆・亜鉛めっきなど溶接に支障となるものを除去する。",
  },

  "7.6.7": {
    plainSummary:
      "溶接は下向き・水平姿勢を基本とし、必要に応じ予熱。完全溶込み溶接・部分溶込み溶接には鋼製エンドタブを用いる。完全溶込みの両面溶接は裏はつり後に裏溶接。隅肉溶接の施工長さは有効長さ＋サイズの2倍。アークストライク禁止。",
    why: "溶接の種類は3つ：完全溶込み（板厚全部を溶かし母材同等の強度）、部分溶込み（一部のみ）、隅肉（入隅を三角に）。柱梁接合部など重要箇所は完全溶込みです。始端・終端は電流が不安定で欠陥が出やすいため、エンドタブという捨て板の上でアークを始め・終わらせ、欠陥を母材の外に逃がします。",
    points: [
      "裏当て金は原則フランジの内側に設置",
      "エンドタブ切断時はグラインダーで粗さ100μmRz程度以下に仕上げ",
      "スパッター（飛散粒）は摩擦面・塗装下地では必ず除去",
    ],
    glossary: [
      {
        term: "エンドタブ",
        meaning:
          "溶接の始端・終端に取り付ける補助板。欠陥の出やすい端部を母材の外に出す。",
      },
      {
        term: "裏はつり（ガウジング）",
        meaning:
          "表側の溶接後、裏から健全な溶着金属が出るまで削ること。裏溶接の前処理。",
      },
      {
        term: "余盛り",
        meaning: "溶接ビードが母材表面より盛り上がった部分。緩やかに、高すぎず。",
      },
    ],
    diagrams: [
      {
        svg: SVG_WELD_TYPES,
        caption: "溶接の3種類（完全溶込み・部分溶込み・隅肉）（7.6.7）",
      },
    ],
  },

  "7.6.8": {
    plainSummary:
      "気温−5℃未満は溶接禁止。−5℃〜5℃は溶接線から100mm程度を加熱すれば可。母材が濡れているとき・強風時も原則禁止（養生すれば可）。",
    why: "低温の母材は溶接部が急冷され、硬化・割れ（低温割れ）が起こります。水分は水素割れの原因、風はシールドガスを飛ばして欠陥を作ります。",
    diagrams: [
      {
        svg: SVG_WELD_TEMP,
        caption: "気温と溶接可否（7.6.8）",
      },
    ],
  },

  "7.6.9": {
    plainSummary:
      "設備金物などを鉄骨に溶接する場合も、最小ビード長さを守り、必要なら予熱し、資格者が行う。母材への悪影響を防ぐ。",
    why: "「ちょっと吊りピースを付けるだけ」の小さな溶接が、母材の材質を傷める事故につながります。関連工事の溶接も本体と同じルールです。",
  },

  "7.6.10": {
    plainSummary:
      "溶接の着手前（開先精度・清掃・予熱・エンドタブ）、作業中（電流・電圧・パス間温度）、完了後（外観・寸法・内部欠陥）に確認を行い、記録を監督職員に提出する。",
    points: [
      "「前・中・後」の3段階確認が溶接品質管理の基本形",
      "パス間温度（多層溶接の層間温度）の管理は強度に直結",
    ],
    diagrams: [
      {
        svg: SVG_WELD_DEFECTS,
        caption: "代表的な溶接欠陥の名称（7.6.10・7.6.12）",
      },
    ],
  },

  "7.6.11": {
    plainSummary:
      "溶接部の試験（検査）は、鉄骨製作工場に所属せず、その工事の品質管理をしていない第三者の試験機関の技能資格者が行う。UT等はJIS Z 2305の資格者。",
    why: "鉄筋のガス圧接検査と同じ「作った人が採点しない」原則です。第三者検査機関の体制・機器・実績の資料を提出して承諾を受けます。",
  },

  "7.6.12": {
    plainSummary:
      "外観試験（告示1464号・JASS 6基準）と、完全溶込み溶接部の超音波探傷試験を行う。工場溶接は抜取り（1ロットからサンプル20箇所、合否判定は表7.6.3）、現場溶接は全数試験。不合格箇所は補修して再試験。",
    points: [
      "工場溶接＝抜取りUT、現場溶接＝全数UT の違いを覚える",
      "ロット不合格なら残り全数試験",
      "割れの疑いには浸透探傷（PT）・磁粉探傷（MT）",
    ],
  },

  "7.6.13": {
    plainSummary:
      "不合格の補修：外観不良→修正、内部欠陥→除去して再溶接、アンダーカット等→補修溶接、余盛り過大→除去・整形、割れ→全長除去（範囲特定できれば割れ端から50mm以上除去）して再溶接。補修後は再確認・再試験。",
    points: [
      "割れは「削って埋める」ではなく「割れ＋50mm以上を除去して再溶接」",
      "補修用溶接棒は手溶接でφ4mm以下",
      "母材に割れが及んだら監督職員と協議",
    ],
  },

  "7.7.1": {
    plainSummary: "この節はスタッド溶接とデッキプレートの溶接に適用する。",
  },

  "7.7.2": {
    plainSummary:
      "スタッド溶接は技能資格者が行う。能力に疑いが生じた場合は試験で判定する。",
  },

  "7.7.3": {
    plainSummary:
      "スタッドの仕上り基準：高さ±2mm、傾き5°以内、カラーが全周に形成、アンダーカット0.5mm以内。",
    glossary: [
      {
        term: "カラー",
        meaning:
          "スタッド溶接後、根元にできる余盛りの輪。全周にあれば健全な溶接の証拠。",
      },
    ],
    diagrams: [
      {
        svg: SVG_STUD,
        caption: "スタッドの仕上り基準と打撃曲げ試験（7.7.3・7.7.6）",
      },
    ],
  },

  "7.7.4": {
    plainSummary:
      "スタッド溶接はアークスタッド溶接の直接溶接・下向き姿勢が原則。午前・午後の作業開始前、装置移動時、径が変わるごとに2本以上の試験溶接（30°打撃曲げ）で条件確認する。",
    points: [
      "「午前・午後・装置替え・径替え」＝試験溶接のタイミング",
      "溶接面の錆・塗料・めっきは軸径の2倍以上の範囲を除去",
    ],
  },

  "7.7.5": {
    plainSummary:
      "気温0℃以下はスタッド溶接禁止（100mm範囲を36℃程度に加熱すれば可）。母材が濡れているときも禁止。",
  },

  "7.7.6": {
    plainSummary:
      "完了後は全数の外観確認（アンダーカット・カラー）＋抜取り試験。100本＋端数を1ロットとし、1本以上を抜き取って高さ・傾きを計測、15°打撃曲げ試験で割れがなければロット合格。",
    points: [
      "打撃曲げで合格したスタッドは曲がったまま使ってよい",
      "不合格→同一ロットからさらに2本試験→2本合格でロット合格",
    ],
  },

  "7.7.7": {
    plainSummary:
      "不合格スタッドは隣接部に打直し。欠陥が母材に及んでいる場合は除去→予熱補修溶接→グラインダー仕上げ→打直し。打直し後は全数試験して記録を提出。",
  },

  "7.7.8": {
    plainSummary: "デッキプレートと鉄骨部材の溶接方法（焼抜き栓溶接等）は特記による。",
  },

  "7.8.1": {
    plainSummary:
      "この節は鉄骨の錆止め塗装に適用する。ここにない事項は18章（塗装工事）による。",
  },

  "7.8.2": {
    plainSummary:
      "塗装しない部分が決まっている：①コンクリートに密着・埋込みの部分 ②高力ボルト摩擦接合の摩擦面 ③密閉される閉鎖形断面の内面 ④ピン・ローラー等の削り仕上げ面 ⑤肌合せ部分。現場溶接部の両側100mm程度はUT完了後に塗装。",
    why: "「塗ってはいけない場所」を問う知識は実務・試験の定番です。理由とセットで：コンクリート付着面→付着力低下、摩擦面→すべり係数低下、閉鎖断面内面→空気がなく錆びない、溶接部→溶接欠陥と検査障害の原因。",
    points: [
      "現場溶接部の両側100mm程度は「溶接とUTが終わってから」塗る",
    ],
  },

  "7.8.3": {
    plainSummary:
      "現場塗装は、現場接合部の素地ごしらえをC種で行い工場と同種の錆止め塗料で塗装。運搬で傷んだ塗膜は活膜を残して除去し補修する。",
  },

  "7.8.4": {
    plainSummary:
      "SRC造の鋼製スリーブ内面などの錆止め塗料種別は特記（なければAs種）。耐火被覆材が接着する面の塗料種別は特記による。",
  },

  "7.9.1": {
    plainSummary: "この節は鉄骨の耐火被覆に適用する。",
    why: "鋼材は火災の熱（約500℃超）で強度が半減します。耐火被覆は鉄骨造の建物が火災時に倒壊しないための生命線です。",
  },

  "7.9.2": {
    plainSummary:
      "耐火被覆の種類は耐火材吹付け・耐火板張り・耐火材巻付け・ラス張りモルタル塗り・耐火塗料等で、種類・材料・工法は特記による。",
  },

  "7.9.3": {
    plainSummary:
      "耐火性能は特記により、取付け・付着強度が十分であること。貫通孔部、デッキプレートと梁の隙間、取付け金物も適切に被覆する。",
    points: [
      "「隙間」の被覆漏れが耐火上の弱点になる（設備貫通部・デッキの溝）",
    ],
  },

  "7.9.4": {
    plainSummary:
      "耐火材吹付け（ロックウール吹付け等）は大臣認定の材料・工法とし、飛散防止の養生を行う。",
    points: [
      "吹付け厚さの確認ピンによる管理が一般的",
    ],
  },

  "7.9.5": {
    plainSummary: "耐火板張り（けい酸カルシウム板等）は法定または認定の材料・工法。見え掛り面は塗装できるものとする。",
  },

  "7.9.6": {
    plainSummary: "耐火材巻付け（フェルト状材料）は大臣認定の材料・工法による。",
  },

  "7.9.7": {
    plainSummary: "ラス張りモルタル塗りは法に基づく塗厚を確保する。",
  },

  "7.9.8": {
    plainSummary:
      "耐火塗料（火災時に発泡して断熱層を作る塗料）は大臣認定の材料・工法による。",
  },

  "7.9.9": {
    plainSummary: "耐火被覆の種類に応じて所定の試験（厚さ・密度等）を行う。",
  },

  "7.10.1": {
    plainSummary: "この節は鉄骨の工事現場施工（建方）に適用する。",
  },

  "7.10.2": {
    plainSummary: "建方精度はJASS 6の鉄骨精度検査基準（工事現場）による。",
  },

  "7.10.3": {
    plainSummary:
      "アンカーボルトは型板で心出しし、二重ナット＋座金でねじ3山以上出す。建方用アンカーボルトの保持はA種（支持枠で上下固定）が標準。柱底均しモルタルはA種（中央塗り＋無収縮モルタル圧入）が標準で、コンクリート面は目荒しする。",
    why: "アンカーボルトの位置精度が建方全体の精度を決めます。コンクリート打込み時に動かないよう堅固に固定し、ねじ部は養生します。ベースプレート下のモルタルは柱の力を基礎に伝える重要部で、無収縮モルタルを隙間なく圧入するA種が標準です。",
    points: [
      "アンカーボルトのねじ部はテープ・布で養生（錆・モルタル付着防止）",
      "「二重ナット＋3山以上」はここでも共通ルール",
    ],
    diagrams: [
      {
        svg: SVG_BASE,
        caption: "柱底均しモルタルA種工法（表7.10.2）",
      },
    ],
  },

  "7.10.4": {
    plainSummary:
      "製品の現場搬入時は必要に応じて養生。曲がり・ねじれが生じた部材は建方前に修正する。",
  },

  "7.10.5": {
    plainSummary:
      "建方は本接合完了まで風・自重に安全な方法で。仮ボルトは1群の1/3以上かつ2本以上（混用・併用継手は1/2以上かつ2本以上、現場溶接のエレクションピースは高力ボルトで全数）。本接合前に建入れ直しを行い、建方完了後は監督職員の検査を受ける。",
    why: "建方中の鉄骨は接合が完了しておらず、強風で倒壊した事故が実際に起きています。仮ボルトの本数規定は建方中の安全の最低ラインです。「1/3・1/2・全数」の3つの数字はセットで覚えましょう。",
    points: [
      "仮ボルトに本接合用の高力ボルトを流用しない",
      "建入れ直しは本接合前に（締めてからでは直らない）",
      "重量物を鉄骨に載せるときは補強検討結果を提出",
    ],
    glossary: [
      {
        term: "建入れ直し",
        meaning: "建方後、柱の垂直度などを測定してワイヤ等で修正する作業。",
      },
      {
        term: "エレクションピース",
        meaning:
          "柱の現場溶接継手部に付ける仮固定用のプレート。溶接完了後に切除する。",
      },
    ],
    diagrams: [
      {
        svg: SVG_KARI_BOLT,
        caption: "仮ボルトの本数規定（7.10.5）",
      },
    ],
  },

  "7.11.1": {
    plainSummary:
      "この節は冷間成形の軽量形鋼（リップ溝形鋼など）を使う場合に適用する。",
  },

  "7.11.2": {
    plainSummary:
      "軽量形鋼の切断は機械切断。管形部材の防錆上必要な端部には同質材のふたをする。",
    why: "軽量形鋼は板厚が薄く、ガス切断では熱で歪み・溶損が大きいため機械切断とします。閉鎖断面の内部は塗装できないので、ふたで湿気を遮断します。",
  },

  "7.12.1": {
    plainSummary: "この節は溶融亜鉛めっきを施した鉄骨を使う工事に適用する。",
    glossary: [
      {
        term: "溶融亜鉛めっき（どぶ漬け）",
        meaning:
          "溶けた亜鉛の槽に鋼材を浸けて表面に亜鉛層を作る防錆処理。屋外露出の鉄骨に使う。",
      },
    ],
  },

  "7.12.2": {
    plainSummary:
      "溶融亜鉛めっき高力ボルトの締付け作業には施工管理技術者を配置する。",
  },

  "7.12.3": {
    plainSummary: "めっき高力ボルトの締付け作業は技能資格者が行う。",
  },

  "7.12.4": {
    plainSummary:
      "めっき作業はJISマーク表示認証工場で行う。溶接面にはめっきを付着させない。溶接部に割れ等を認めたら監督職員と協議。",
    why: "めっき槽（約450℃）に浸けると溶接部の残留応力で「めっき割れ」が生じることがあります。外観試験での割れチェックが重要です。",
  },

  "7.12.5": {
    plainSummary:
      "めっき高力ボルト接合の摩擦面はすべり係数0.4以上（通常の0.45と違う）。処理はめっき後のブラスト処理（50μmRz以上）またはりん酸塩処理。本締めはナット回転法。",
    points: [
      "すべり係数：通常0.45、めっきは0.4 と使い分けて覚える",
      "めっき面はトルク係数が安定しないため本締めはナット回転法",
    ],
  },

  "7.12.6": {
    plainSummary:
      "建入れ直しではめっき面を傷つけないよう養生。傷が付いた場合は高濃度亜鉛末塗料等（表14.2.4）で補修する。",
  },

  "7.12.7": {
    plainSummary:
      "めっき高力ボルトの締付け後確認は、JIS形ナット回転法の確認方法（マークずれ・回転量±30°・余長1〜6山）に準じて行う。",
  },
};

export default ch07;
